import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';

const EmlViewerDialog = ({ open, onClose, fileId, title, sentAt }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && fileId) {
      setLoading(true);
      setError(null);
      setData(null);
      const token = localStorage.getItem('token');
      fetch(`${window.location.origin}/api/files/eml-preview/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error('Failed to load email preview');
          return r.json();
        })
        .then((d) => setData(d))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [open, fileId]);

  const htmlContent = data?.html_body || (data?.text_body
    ? `<pre style="font-family:Arial,sans-serif;padding:16px;white-space:pre-wrap;word-wrap:break-word">${data.text_body}</pre>`
    : null);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle>{title || 'Email Preview'}</DialogTitle>
          {data && (
            <div className="text-xs text-muted-foreground space-y-1 mt-2">
              {data.subject && (
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-16 shrink-0">Subject:</span>
                  <span>{data.subject}</span>
                </div>
              )}
              {data.from_addr && (
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-16 shrink-0">From:</span>
                  <span>{data.from_addr}</span>
                </div>
              )}
              {data.to_addr && (
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-16 shrink-0">To:</span>
                  <span>{data.to_addr}</span>
                </div>
              )}
              {(sentAt || data.date) && (
                <div className="flex gap-2">
                  <span className="font-semibold text-foreground w-16 shrink-0">Sent:</span>
                  <span>{new Date(sentAt || data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}
          {error && (
            <div className="flex justify-center items-center h-64 text-muted-foreground text-sm px-6">
              {error}
            </div>
          )}
          {!loading && !error && htmlContent && (
            <iframe
              srcDoc={htmlContent}
              sandbox="allow-same-origin"
              title="Email Preview"
              className="w-full border-0"
              style={{ height: 'min(60vh, 600px)' }}
            />
          )}
          {!loading && !error && !htmlContent && data && (
            <div className="flex justify-center items-center h-64 text-muted-foreground text-sm">
              No email body content available
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmlViewerDialog;
